import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Code2, Package, PlayCircle, ShieldCheck, StopCircle, Rocket } from 'lucide-react';
import '../../styles/library-page.scss';
import Tooltip from '../ToolTip';

const scrollToStep = (targetId) => {
	const target = document.getElementById(targetId);
	if (!target) return;

	target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const GetStartedShell = ({
	children,
	title,
	description,
	steps = [],
}) => {
	const { pathname } = useLocation();
	const { t } = useTranslation();
	const isCdn = pathname === '/';
	const isNpm = pathname === '/npm';

	return (
		<div className="library-page">
			<div className="library-page__container">
				<div className="library-page__header">
					<h1 className="library-page__title">{title}</h1>
					<p className="library-page__description">{description}</p>
				</div>

				<div className="library-page__tabs">
					<div className="library-page__tab-container">
						<Link
							to="/"
							className={`library-page__tab library-page__tab--stacked${isCdn ? ' library-page__tab--active' : ''}`}
						>
							<span className="library-page__tab-row">
								<Code2 className="library-page__tab-icon" aria-hidden="true" />
								<span className="library-page__tab-label">{t('cdn_tab_title')}</span>
							</span>
							<span className="library-page__tab-hint">{t('cdn_tab_desc')}</span>
						</Link>
						<Link
							to="/npm"
							className={`library-page__tab library-page__tab--stacked${isNpm ? ' library-page__tab--active' : ''}`}
						>
							<span className="library-page__tab-row">
								<Package className="library-page__tab-icon" aria-hidden="true" />
								<span className="library-page__tab-label">{t('npm_tab_title')}</span>
							</span>
							<span className="library-page__tab-hint">{t('npm_tab_desc')}</span>
						</Link>
					</div>
				</div>

				{steps.length > 0 && (
					<div className="library-page__steps">
						{steps.map((step) => (
							<button
								key={step.number}
								type="button"
								className="library-page__step"
								onClick={() => scrollToStep(step.targetId)}
								aria-label={step.title}
							>
								<div className="library-page__step-header">
									<div className="library-page__step-icon">
										<step.icon />
									</div>
									<span className="library-page__step-number">{step.number}</span>
								</div>
								<h3 className="library-page__step-title">{step.title}</h3>
								<p className="library-page__step-description">{step.description}</p>
							</button>
						))}
					</div>
				)}

				{children}
			</div>
		</div>
	);
};

export const CopyButton = ({ onCopy, label }) => (
	<div className="library-page__code-toolbar">
		<Tooltip message="Copied to clipboard!">
			<button type="button" className="library-page__copy" onClick={onCopy}>
				{label}
			</button>
		</Tooltip>
	</div>
);

export const CodeCard = ({ children, tabs = null }) => (
	<div className="library-page__code-card">
		{tabs}
		<pre className="library-page__pre">{children}</pre>
	</div>
);

export const getStartedSteps = (t) => [
	{
		number: 1,
		icon: Rocket,
		title: t('init_step_card_title'),
		description: t('init_step_card_desc'),
		targetId: 'get-started-init',
	},
	{
		number: 2,
		icon: ShieldCheck,
		title: t('precheck_step_card_title'),
		description: t('precheck_step_card_desc'),
		targetId: 'get-started-prechecks',
	},
	{
		number: 3,
		icon: PlayCircle,
		title: t('start_session_step_card_title'),
		description: t('start_session_step_card_desc'),
		targetId: 'get-started-start-session',
	},
	{
		number: 4,
		icon: StopCircle,
		title: t('stop_session_step_card_title'),
		description: t('stop_session_step_card_desc'),
		targetId: 'get-started-stop-session',
	},
];

export default GetStartedShell;
